import React from 'react';
import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import MultiSelectAutoCompleteFormik from '~/components/core/Formik/MultiSelectAutoCompleteFormik/MultiSelectAutoCompleteFormik';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import Text from '~/components/core/TextComponents/Text';
import useOrganization from '~/components/OrganizationContext';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import enums from '~/server_shared/enums.json';

import styles from '../../PaymentsConfigurationForm.module.scss';

// Helper function to calculate initial values for multi-currency configuration
const getMultiCurrencyInitialValues = (
  paymentsConfiguration: PaymentsConfiguration,
  subOrganizations: SubOrganization[],
  isSubOrgsEnabled: boolean
): FormValues => {
  const existingConfig = paymentsConfiguration?.multi_currency_configuration || {};

  if (!isSubOrgsEnabled) {
    // Organization level configuration
    return {
      organization_currencies: existingConfig.organization_currencies || [],
    };
  } else {
    // Sub-organization level configuration
    const subOrgConfigs: SubOrganizationCurrencyConfig[] = (subOrganizations || []).map((subOrg: SubOrganization) => {
      const existingSubOrgConfig = existingConfig.sub_organizations?.find(
        (config) => config.sub_organization_id === subOrg.id
      );

      return {
        sub_organization_id: subOrg.id,
        enabled: existingSubOrgConfig?.enabled || false,
        currencies: existingSubOrgConfig?.currencies || [],
      };
    });

    return {
      sub_organizations: subOrgConfigs,
    };
  }
};

// Helper function to create validation schema for multi-currency configuration
const getMultiCurrencyValidationSchema = (isSubOrgsEnabled: boolean) => {
  if (!isSubOrgsEnabled) {
    return Yup.object().shape({
      organization_currencies: Yup.array()
        .min(1, 'At least one currency must be selected')
        .required('Please select at least one currency'),
    });
  } else {
    return Yup.object().shape({
      sub_organizations: Yup.array().of(
        Yup.object().shape({
          sub_organization_id: Yup.number().required(),
          enabled: Yup.boolean().required(),
          currencies: Yup.array().when('enabled', {
            is: true,
            then: Yup.array()
              .min(1, 'At least one currency must be selected')
              .required('Please select at least one currency'),
            otherwise: Yup.array(),
          }),
        })
      ),
    });
  }
};

interface SubOrganization {
  id: number;
  name: string;
}

interface SubOrganizationCurrencyConfig {
  sub_organization_id: number;
  enabled: boolean;
  currencies: string[];
}

interface MultiCurrencyConfiguration {
  organization_currencies?: string[];
  sub_organizations?: SubOrganizationCurrencyConfig[];
}

interface PaymentsConfiguration {
  multi_currency_configuration?: MultiCurrencyConfiguration;
}

interface MultiCurrencyConfigurationDialogProps {
  paymentsConfiguration: PaymentsConfiguration;
  disabled?: boolean;
  onSubmit: (config: {
    multi_currency_configuration: MultiCurrencyConfiguration;
    allow_claim_multi_currency: boolean;
  }) => Promise<void>;
  onCancel: () => void;
}

interface FormValues {
  organization_currencies?: string[];
  sub_organizations?: SubOrganizationCurrencyConfig[];
}

const MultiCurrencyConfigurationDialog: React.FC<MultiCurrencyConfigurationDialogProps> = ({
  paymentsConfiguration,
  disabled = false,
  onCancel,
  onSubmit,
}) => {
  const { classes } = useStyles();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const { subOrganizations } = useOrganization() as { subOrganizations: SubOrganization[] };

  const isSubOrgsEnabled = organization?.sub_organizations_enabled;

  return (
    <Formik<FormValues>
      initialValues={getMultiCurrencyInitialValues(paymentsConfiguration, subOrganizations, isSubOrgsEnabled)}
      validationSchema={getMultiCurrencyValidationSchema(isSubOrgsEnabled)}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          const configPayload = {
            multi_currency_configuration: values,
            allow_claim_multi_currency: true,
          };
          await onSubmit(configPayload);
        } catch (error) {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, values }: FormikProps<FormValues>) => (
        <CardDialog
          title="Allow claim multi-currency"
          isDialog
          maxWidth="md"
          fullWidth
          onClose={onCancel}
          preventClose={disabled}
        >
          <div className="space-y-4">
            {!isSubOrgsEnabled ? (
              <div className="w-full">
                <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.MEDIUM}>
                  Select allowed currencies for the organization:
                </Text>
                <div className="w-3/4">
                  <MultiSelectAutoCompleteFormik
                    id="organization_currencies"
                    label="Supported Currencies"
                    options={enums.SUPPORTED_CURRENCIES}
                    getOptionLabel={(currency: string) => currency}
                    className={styles.filterContainer}
                  />
                </div>
              </div>
            ) : (
              <>
                <div className="w-full">
                  <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.MEDIUM}>
                    Configure multi-currency per sub-organization:
                  </Text>
                </div>
                {(values.sub_organizations || []).map((subOrgConfig, index) => {
                  const subOrg = subOrganizations?.find((org) => org.id === subOrgConfig.sub_organization_id);
                  if (!subOrg) return null;

                  return (
                    <div key={subOrg.id} className="w-full space-y-4 rounded-lg border p-4">
                      <div className="flex items-center space-x-4">
                        <SwitchFormik
                          id={`sub_organizations.${index}.enabled`}
                          label={`Enable multi-currency for ${subOrg.name}`}
                        />
                      </div>
                      {subOrgConfig.enabled && (
                        <div className="ml-8">
                          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.MEDIUM}>
                            Select allowed currencies for {subOrg.name}:
                          </Text>
                          <div className="mt-2 w-3/4">
                            <MultiSelectAutoCompleteFormik
                              id={`sub_organizations.${index}.currencies`}
                              label={`Currencies for ${subOrg.name}`}
                              options={enums.SUPPORTED_CURRENCIES}
                              getOptionLabel={(currency: string) => currency}
                              className={styles.filterContainer}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <div className={classes.buttonsContainer}>
            <CancelButton disabled={disabled} onClick={onCancel} />
            <Button variant="contained" color="primary" disabled={disabled} onClick={() => handleSubmit()}>
              Save
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default MultiCurrencyConfigurationDialog;
